import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { getDatabase, ref, onValue, remove, get } from 'firebase/database'
import { useSelector, useDispatch } from 'react-redux'

import { createNewConfigId } from '../../lib/utilities/createNewConfigId';
import { hydrateConfigToRedux } from '../../lib/utilities/hydrateConfigToRedux';
import { resetAll } from '../../reduxSlices/results/resultsSlice';
import { deleteConfigFilesFromStorage } from '../../lib/utilities/deleteConfigFilesFromStorage';
import Loader from '../shared/Loader';
import LoadingSpinner from '../shared/LoadingSpinner';
import ConfirmationModal from '../shared/ConfirmationModal';
import toast, { Toaster } from 'react-hot-toast';



function StartConfig() {
  const navigate = useNavigate()
  const dispatch = useDispatch();
  const uid = useSelector(state => state.auth?.user?.id)
  const [configs, setConfigs] = useState([])
  const [creatorInfo, setCreatorInfo] = useState({ email: '', role: '' });
  const [loading, setLoading] = useState(false); // Loading state for view button
  const [loadingConfigs, setLoadingConfigs] = useState(true); // Loading state for configs table
  const [progress, setProgress] = useState(undefined); // Progress for Loader
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [configToDelete, setConfigToDelete] = useState(null);

  // View config handler
  const handleView = async (configId) => {
    try {
      setLoading(true); // Start loading
      setProgress(0);
      localStorage.setItem('currentConfigId', configId);
      await hydrateConfigToRedux(configId, uid, dispatch, setProgress);
      navigate('/configuration/showAllFiles', { state: { skipAutoSave: true } });
    } catch (err) {
      alert('Failed to load configuration: ' + err.message);
    } finally {
      setLoading(false); // Stop loading
      setProgress(undefined);
    }
  }

  useEffect(() => {
    if (!uid) return;
    setLoadingConfigs(true); // Start loading configs
    const db = getDatabase();
    // Fetch configs
    const resultsRef = ref(db, `users/${uid}/results`);
    const unsubscribeResults = onValue(resultsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const entries = Object.keys(data).map(key => ({
          id: key,
          ...data[key]
        }));
        setConfigs(entries);
      } else {
        setConfigs([]);
      }
      setLoadingConfigs(false); // Stop loading configs
    });

    // Fetch creator info (email, role)
    const userRef = ref(db, `users/${uid}`);
    const unsubscribeUser = onValue(userRef, (snapshot) => {
      const data = snapshot.val();
      setCreatorInfo({
        email: data?.email || '',
        role: data?.role || ''
      });
    });

    return () => {
      unsubscribeResults();
      unsubscribeUser();
    };
  }, [uid]);

  const handleDelete = async (configId) => {
    if (!uid || !configId) return;
    const db = getDatabase();
    // Fetch config data from DB to get file URLs
    const configRef = ref(db, `users/${uid}/results/${configId}`);
    const snapshot = await get(configRef);
    const configData = snapshot.exists() ? snapshot.val() : null;
    if (configData) {
      await deleteConfigFilesFromStorage(uid, configId, configData);
    }
    await remove(configRef);
  }

  const handleDeleteClick = (configId) => {
    setConfigToDelete(configId);
    setDeleteModalOpen(true);
  }

  const confirmDelete = async () => {
    if (configToDelete) {
      await handleDelete(configToDelete);
      setConfigToDelete(null);
      toast.success("Configuration deleted successfully!");
    }
  }

  return (
    <div className='flex flex-col items-center justify-center mt-6'>
      <Toaster position="top-center" />
      <ConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        title="Delete Configuration"
        message="Are you sure you want to delete this configuration? This action cannot be undone and will remove all associated files."
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
      {loading && <Loader text="Loading from database..." progress={progress} />}
      <h1 className='text-lg self-start pl-4 text-customeBlack'>Click on 'Start a New Configuration' button to start the configuration Process.</h1>



      <button
        onClick={() => {
          dispatch(resetAll());
          createNewConfigId();
          navigate('/configuration/uploadStdFiles');
        }}
        className='mt-40 flex-row flex items-center justify-center bg-buttonColorActive text-white w-60 p-3 rounded-md self-center disabled:bg-gray-300 disabled:text-gray-500 disabled:border-gray-300 disabled:cursor-not-allowed hover:bg-customeOrange hover:text-customeBlack'
      >
        Start a New Configuration
      </button>


      {loadingConfigs ? (
        <LoadingSpinner
          text="Loading configurations..."
          size="md"
          className="w-4/5 h-auto mt-96"
        />
      ) : configs.length > 0 && (
        <div className="w-4/5 h-auto mt-96">
          <table className="border-collapse w-full">
            <thead>
              <tr>
                <th className="bg-customeBlack text-white border-white border">Saved Configurations</th>
                <th className="bg-customeBlack text-white border-white border">Last Updated</th>
                <th className="bg-customeBlack text-white border-white border">Created by</th>
                <th className="bg-customeBlack text-white border-white border">Actions</th>
              </tr>
            </thead>
            <tbody>
              {configs.map(config => (
                <tr key={config.id}>
                  <td className="border p-2 break-all">{config.id}</td>
                  <td className="border p-2">
                    {config.lastUpdated
                      ? new Intl.DateTimeFormat('en-GB', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: true,
                      }).format(new Date(config.lastUpdated))
                      : 'N/A'}
                  </td>
                  <td className="border p-2">
                    {creatorInfo.email ? creatorInfo.email : (creatorInfo.role || '—')}
                  </td>
                  <td className="border p-2 flex gap-2">
                    <button
                      onClick={() => handleDeleteClick(config.id)}
                      className="text-white bg-red-600 px-2 py-1 rounded hover:bg-red-800"
                    >
                      Delete
                    </button>
                    <button
                      onClick={() => handleView(config.id)}
                      className="text-white bg-blue-600 px-2 py-1 rounded hover:bg-blue-800"
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

    </div>
  )
}

export default StartConfig